// The wall-clock time AT a place, for "it was 02:40 there". The browser's Intl
// already knows every IANA zone and its DST rules; what it can't know is which
// zone a point is in. That comes from the country under the point
// (GET /api/boundaries/country) and the table below — single-zone countries
// only. A country spanning several zones (RUS, USA, BRA, IDN, ...) or with
// offshore territory on another clock (ESP's Canaries, PRT's Azores) is left
// out on purpose: rather than guess a zone, it falls back to local MEAN SOLAR
// time from the longitude, and says so.

import { get } from './api';

const ZONE_BY_ISO3: Record<string, string> = {
  // Middle East, Caucasus, Central and South Asia
  IRN: 'Asia/Tehran', IRQ: 'Asia/Baghdad', SYR: 'Asia/Damascus', LBN: 'Asia/Beirut',
  ISR: 'Asia/Jerusalem', PSE: 'Asia/Gaza', JOR: 'Asia/Amman', SAU: 'Asia/Riyadh',
  YEM: 'Asia/Aden', OMN: 'Asia/Muscat', ARE: 'Asia/Dubai', QAT: 'Asia/Qatar',
  BHR: 'Asia/Bahrain', KWT: 'Asia/Kuwait', TUR: 'Europe/Istanbul', CYP: 'Asia/Nicosia',
  AFG: 'Asia/Kabul', PAK: 'Asia/Karachi', IND: 'Asia/Kolkata', AZE: 'Asia/Baku',
  ARM: 'Asia/Yerevan', GEO: 'Asia/Tbilisi', TKM: 'Asia/Ashgabat', UZB: 'Asia/Tashkent',
  TJK: 'Asia/Dushanbe', KGZ: 'Asia/Bishkek', KAZ: 'Asia/Almaty', NPL: 'Asia/Kathmandu',
  BGD: 'Asia/Dhaka', BTN: 'Asia/Thimphu', LKA: 'Asia/Colombo', MDV: 'Indian/Maldives',
  // East and South-East Asia
  CHN: 'Asia/Shanghai', PRK: 'Asia/Pyongyang', KOR: 'Asia/Seoul', JPN: 'Asia/Tokyo',
  TWN: 'Asia/Taipei', VNM: 'Asia/Ho_Chi_Minh', LAO: 'Asia/Vientiane', KHM: 'Asia/Phnom_Penh',
  THA: 'Asia/Bangkok', MMR: 'Asia/Yangon', MYS: 'Asia/Kuala_Lumpur', SGP: 'Asia/Singapore',
  PHL: 'Asia/Manila',
  // Africa
  EGY: 'Africa/Cairo', SDN: 'Africa/Khartoum', SSD: 'Africa/Juba', ETH: 'Africa/Addis_Ababa',
  ERI: 'Africa/Asmara', DJI: 'Africa/Djibouti', SOM: 'Africa/Mogadishu', KEN: 'Africa/Nairobi',
  UGA: 'Africa/Kampala', TZA: 'Africa/Dar_es_Salaam', RWA: 'Africa/Kigali', BDI: 'Africa/Bujumbura',
  LBY: 'Africa/Tripoli', TUN: 'Africa/Tunis', DZA: 'Africa/Algiers', MAR: 'Africa/Casablanca',
  ESH: 'Africa/El_Aaiun', MRT: 'Africa/Nouakchott', MLI: 'Africa/Bamako', NER: 'Africa/Niamey',
  TCD: 'Africa/Ndjamena', NGA: 'Africa/Lagos', CMR: 'Africa/Douala', CAF: 'Africa/Bangui',
  BFA: 'Africa/Ouagadougou', SEN: 'Africa/Dakar', GHA: 'Africa/Accra', CIV: 'Africa/Abidjan',
  BEN: 'Africa/Porto-Novo', TGO: 'Africa/Lome', GIN: 'Africa/Conakry', SLE: 'Africa/Freetown',
  LBR: 'Africa/Monrovia', GMB: 'Africa/Banjul', GNB: 'Africa/Bissau', GAB: 'Africa/Libreville',
  COG: 'Africa/Brazzaville', GNQ: 'Africa/Malabo', AGO: 'Africa/Luanda', ZMB: 'Africa/Lusaka',
  ZWE: 'Africa/Harare', MWI: 'Africa/Blantyre', MOZ: 'Africa/Maputo', NAM: 'Africa/Windhoek',
  BWA: 'Africa/Gaborone', ZAF: 'Africa/Johannesburg', LSO: 'Africa/Maseru', SWZ: 'Africa/Mbabane',
  MDG: 'Indian/Antananarivo',
  // Europe
  UKR: 'Europe/Kyiv', BLR: 'Europe/Minsk', MDA: 'Europe/Chisinau', POL: 'Europe/Warsaw',
  ROU: 'Europe/Bucharest', BGR: 'Europe/Sofia', GRC: 'Europe/Athens', SRB: 'Europe/Belgrade',
  XKX: 'Europe/Belgrade', HRV: 'Europe/Zagreb', BIH: 'Europe/Sarajevo', MNE: 'Europe/Podgorica',
  ALB: 'Europe/Tirane', MKD: 'Europe/Skopje', HUN: 'Europe/Budapest', SVK: 'Europe/Bratislava',
  CZE: 'Europe/Prague', AUT: 'Europe/Vienna', DEU: 'Europe/Berlin', CHE: 'Europe/Zurich',
  ITA: 'Europe/Rome', FRA: 'Europe/Paris', BEL: 'Europe/Brussels', NLD: 'Europe/Amsterdam',
  LUX: 'Europe/Luxembourg', GBR: 'Europe/London', IRL: 'Europe/Dublin', ISL: 'Atlantic/Reykjavik',
  NOR: 'Europe/Oslo', SWE: 'Europe/Stockholm', FIN: 'Europe/Helsinki', EST: 'Europe/Tallinn',
  LVA: 'Europe/Riga', LTU: 'Europe/Vilnius', DNK: 'Europe/Copenhagen', SVN: 'Europe/Ljubljana',
  MLT: 'Europe/Malta',
  // Americas, Oceania
  COL: 'America/Bogota', VEN: 'America/Caracas', PER: 'America/Lima', BOL: 'America/La_Paz',
  ARG: 'America/Argentina/Buenos_Aires', URY: 'America/Montevideo', PRY: 'America/Asuncion',
  CUB: 'America/Havana', HTI: 'America/Port-au-Prince', DOM: 'America/Santo_Domingo',
  GTM: 'America/Guatemala', HND: 'America/Tegucigalpa', SLV: 'America/El_Salvador',
  NIC: 'America/Managua', CRI: 'America/Costa_Rica', PAN: 'America/Panama', JAM: 'America/Jamaica',
  NZL: 'Pacific/Auckland',
};

/** The IANA zone for a country, or null when it has no single clock (or an
 *  old browser doesn't know the zone name). */
export function zoneForIso(iso: string | null | undefined): string | null {
  const zone = iso ? ZONE_BY_ISO3[iso.toUpperCase()] : undefined;
  if (!zone) return null;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: zone });
    return zone;
  } catch {
    return null;
  }
}

/** The local clock at a place, rendered: "Oct 23, 2:40 AM GMT+3:30", or
 *  "Oct 23, 2:13 AM solar" when no civil zone is known. */
export function localClock(ms: number, lon: number, zone: string | null): string {
  if (zone) {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZone: zone, timeZoneName: 'shortOffset',
    }).format(new Date(ms));
  }
  // Mean solar time: 15° of longitude per hour from Greenwich, formatted as
  // if it were UTC. Off the civil clock by up to an hour or so — hence the word.
  const solar = new Date(ms + (lon / 15) * 3_600_000);
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
  }).format(solar) + ' solar';
}

// Country lookups, cached by 0.1° cell; a miss (open sea, no CGAZ loaded,
// network failure) caches as null and falls back to solar time.
const zoneCache = new Map<string, Promise<string | null>>();

export function zoneAt(lon: number, lat: number): Promise<string | null> {
  const key = `${lon.toFixed(1)},${lat.toFixed(1)}`;
  let hit = zoneCache.get(key);
  if (!hit) {
    hit = get<{ iso: string }>(`/api/boundaries/country?lon=${lon}&lat=${lat}`)
      .then((r) => zoneForIso(r.iso), () => null);
    zoneCache.set(key, hit);
  }
  return hit;
}
